/**
 * Brand page FAQ — the ONE builder behind the visible accordion
 * (app/brands/[slug]/brand-client.tsx) and the FAQPage JSON-LD
 * (components/seo/brand-schemas.tsx).
 *
 * FRIDGE-257 measured 4 question mismatches on /brands/lg between the two
 * surfaces because each file carried its own copy of the heuristics: the
 * schema asked "How many … refrigerators and freezers are available on
 * Fridge.com?" while the page asked "How many … refrigerators are on
 * Fridge.com?", and the page's Energy Star / price answers never reached the
 * schema at all. Both surfaces now call buildBrandFaqs(); the schema surface
 * filters with `forSchema` (see BRAND_FAQ_SCHEMA_INCLUDES_PRICE_ANSWERS).
 *
 * Every figure comes from the stats the server reads out of `products` at
 * render — nothing here is typed in.
 */

import { formatPrice } from '@/lib/format-utils'

export interface BrandFaqInput {
  brand: {
    name: string
    description: string | null
    parent_company: string | null
    headquarters?: string | null
    founding_year: number | null
  }
  stats: {
    productCount: number
    minPrice: number | null
    maxPrice: number | null
    avgPrice?: number | null
    types: string[]
    energyStarCount?: number
  }
  cheapestProduct?: { title: string; price: number } | null
  mostPopularType?: string | null
  compactCount?: number
}

export interface BrandFaq {
  question: string
  answer: string
  /**
   * True when the answer states an offer price taken from
   * products.lowest_price_cents (price range, cheapest product). Visible on the
   * page always; emitted into FAQPage JSON-LD only when
   * BRAND_FAQ_SCHEMA_INCLUDES_PRICE_ANSWERS is true.
   */
  priceClaim: boolean
}

/**
 * OWNER DECISION PENDING (FRIDGE-257 vs FAQ rewrite §2 rule 3).
 *
 * The 07/11/2026 owner-approved change (commit 87c63068) took every
 * lowest_price_cents-based figure OUT of the brand FAQ JSON-LD ("no prices
 * in FAQ text — prices live in the buy box; FAQ text is cached/indexed and
 * goes stale"). FRIDGE-257 done-when #4 (08/07/2026) asks for the
 * price-range and cheapest-product answers to be ADDED to the schema. Those
 * two directions conflict; the lane did not pick. This constant is the single
 * switch: `false` keeps rule 3 (schema ⊆ visible, no price figures in JSON-LD);
 * flip to `true` if the owner rules for FRIDGE-257 #4.
 */
export const BRAND_FAQ_SCHEMA_INCLUDES_PRICE_ANSWERS = false

export function formatBrandTypeLabel(type: string): string {
  return type
    .split('-')
    .map(w => (w.length > 0 ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ')
}

export function buildBrandFaqs(input: BrandFaqInput): BrandFaq[] {
  const { brand, stats, cheapestProduct, mostPopularType, compactCount = 0 } = input
  const items: BrandFaq[] = []

  if (stats.productCount > 0) {
    items.push({
      question: `How many ${brand.name} refrigerators and freezers are on Fridge.com?`,
      answer: `${brand.name} currently has ${stats.productCount.toLocaleString('en-US')} refrigerators and freezers available on Fridge.com across ${stats.types.length} product types.`,
      priceClaim: false,
    })
  }
  if (stats.minPrice && stats.maxPrice) {
    items.push({
      question: `What is the price range for ${brand.name}?`,
      answer: `${brand.name} prices range from ${formatPrice(stats.minPrice, true)} to ${formatPrice(stats.maxPrice, true)}${stats.avgPrice ? `, with an average of ${formatPrice(stats.avgPrice, true)}` : ''}.`,
      priceClaim: true,
    })
  }
  if (stats.types.length > 0) {
    items.push({
      question: `What types of refrigerators does ${brand.name} make?`,
      answer: `${brand.name} offers ${stats.types.map(formatBrandTypeLabel).join(', ')} on Fridge.com.`,
      priceClaim: false,
    })
  }
  if ((stats.energyStarCount ?? 0) > 0) {
    items.push({
      question: `Does ${brand.name} make Energy Star refrigerators?`,
      answer: `Yes, ${stats.energyStarCount} out of ${stats.productCount} ${brand.name} products on Fridge.com are Energy Star certified.`,
      priceClaim: false,
    })
  }
  items.push({
    question: `Where can I buy ${brand.name} refrigerators?`,
    answer: `Compare ${brand.name} prices from major online appliance retailers on Fridge.com.`,
    priceClaim: false,
  })
  if (brand.description) {
    items.push({
      question: `What is ${brand.name} known for?`,
      answer: brand.description,
      priceClaim: false,
    })
  }
  if (brand.parent_company) {
    items.push({
      question: `Who makes ${brand.name} refrigerators?`,
      answer: `${brand.name} is part of the ${brand.parent_company} family of brands.`,
      priceClaim: false,
    })
  }
  if (brand.headquarters) {
    items.push({
      question: `Where is ${brand.name} headquartered?`,
      answer: `${brand.name} is headquartered in ${brand.headquarters}.`,
      priceClaim: false,
    })
  }
  if (brand.founding_year) {
    items.push({
      question: `When was ${brand.name} founded?`,
      answer: `${brand.name} was founded in ${brand.founding_year}.`,
      priceClaim: false,
    })
  }
  if (cheapestProduct) {
    items.push({
      question: `What is the most affordable ${brand.name} refrigerator?`,
      answer: `The most affordable ${brand.name} product on Fridge.com is the ${cheapestProduct.title} at ${formatPrice(cheapestProduct.price, true)}.`,
      priceClaim: true,
    })
  }
  if (mostPopularType) {
    items.push({
      question: `What is the most popular ${brand.name} product type?`,
      answer: `${formatBrandTypeLabel(mostPopularType)} is the most popular ${brand.name} product type on Fridge.com.`,
      priceClaim: false,
    })
  }
  if (compactCount > 0) {
    items.push({
      question: `Does ${brand.name} offer compact or mini fridges?`,
      answer: `Yes, ${brand.name} offers ${compactCount} compact refrigerators on Fridge.com.`,
      priceClaim: false,
    })
  }
  return items
}

/** The subset that goes into FAQPage JSON-LD — always a subset of the visible list. */
export function brandFaqsForSchema(faqs: BrandFaq[]): Array<{ question: string; answer: string }> {
  return faqs
    .filter(f => BRAND_FAQ_SCHEMA_INCLUDES_PRICE_ANSWERS || !f.priceClaim)
    .map(({ question, answer }) => ({ question, answer }))
}
